// Thin client for the livechat admin API. Endpoints and auth are unchanged
// from the old page: Bearer token from getAuthToken(), base
// `${API_ORIGIN}/.netlify/functions/livechat`.
import { getAuthToken } from '../../../lib/api';
import { API_ORIGIN } from '../../../lib/apiBase';
import { ChatMessage, ChatSession } from './types';

export const LIVECHAT_API = `${API_ORIGIN}/.netlify/functions/livechat`;
export const NOTIFICATION_SOUND = '/audio/UIAlert-iPhone_notification_-Elevenlabs.mp3';

export class LiveChatApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

async function request<T extends { success: boolean }>(path: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(`${LIVECHAT_API}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getAuthToken()}`,
      ...(options.headers as Record<string, string> | undefined),
    },
  });
  const data = await res.json().catch(() => ({ success: false })) as T & { error?: string };
  if (res.status === 401) throw new LiveChatApiError(data.error || 'Not authenticated', 401);
  if (!data.success) throw new LiveChatApiError(data.error || 'Request failed', res.status);
  return data;
}

export const fetchSessions = () => request<{ success: boolean; sessions: ChatSession[] }>('/admin/sessions');

export const fetchSessionDetail = (id: string) =>
  request<{ success: boolean; session: ChatSession; messages: ChatMessage[] }>(`/admin/session/${id}`);

export const takeoverSession = (sessionId: string) =>
  request<{ success: boolean }>('/admin/takeover', { method: 'POST', body: JSON.stringify({ sessionId }) });

export const sendChatMessage = (sessionId: string, content: string) =>
  request<{ success: boolean }>('/admin/message', { method: 'POST', body: JSON.stringify({ sessionId, content }) });

export const closeChatSession = (sessionId: string) =>
  request<{ success: boolean }>('/admin/close', { method: 'POST', body: JSON.stringify({ sessionId }) });

/** Best-effort notification chime; silently ignored if the browser blocks autoplay. */
export const playNotificationSound = () => {
  try {
    const audio = new Audio(NOTIFICATION_SOUND);
    audio.volume = 0.5;
    audio.play().catch(() => {});
  } catch {
    // ignore — no audio support
  }
};
